import { createContext, useContext, useEffect, useState, ReactNode } from 'react'
import { getMe, logout as apiLogout } from '@/api/auth'
import { magicLogin } from '@/api/magicLink'
import { rememberCampaign, setUserContext, track } from '@/lib/analytics'
import { setPollsFlow } from '@/onboarding/pollsFlow'

interface User {
  maxAdminID: string
  expiresAt: string
}

interface AuthContextValue {
  user: User | null
  isLoading: boolean
  refetch: () => Promise<void>
  logout: () => Promise<void>
}

const AuthContext = createContext<AuthContextValue | null>(null)

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null)
  const [isLoading, setIsLoading] = useState(true)

  const refetch = async () => {
    setIsLoading(true)
    try {
      const me = await getMe()
      setUser(me)
      setUserContext({ maxAdminID: me.maxAdminID })
    } catch {
      setUser(null)
    } finally {
      setIsLoading(false)
    }
  }

  const logout = async () => {
    try {
      await apiLogout()
    } finally {
      setUser(null)
    }
  }

  useEffect(() => {
    // Magic-link перехват: если в URL есть ?magic=<token> — обмениваем на cookie
    // и редиректимся куда сказал бэк. Делаем hard-reload, чтобы свежий cookie
    // подхватился во всех запросах.
    const params = new URLSearchParams(window.location.search)
    const magic = params.get('magic')
    if (magic) {
      // Polls-onboarding sticky (2026-07-28): сохраняем flow-marker + `ref`
      // в sessionStorage ДО hard-reload'а. `SourcePage` тоже страхует, но там
      // useEffect срабатывает после reload и в некоторых race'ах ключ может
      // не встать вовремя. Здесь — синхронно, до window.location.replace.
      const refPollId = params.get('ref')
      if (refPollId) setPollsFlow(refPollId)
      magicLogin(magic)
        .then(async (res) => {
          // Бэк говорит на какой канал ориентирована рассылка — пиннит его.
          //
          // ВАЖНО: пишем в ДВА key'а:
          //  - tgx-selected-channel-uuid — стандартный persist (для последующих сессий)
          //  - tgx-magic-target-channel — одноразовый **приоритетный** маркер,
          //    ChannelProvider его прочитает и forced-установит, ДАЖЕ если
          //    localStorage['tgx-selected-channel-uuid'] уже был другим до magic-link.
          //    Одноразовый — очищается сразу после применения, чтобы не мешать
          //    последующим переключениям канала юзером через picker.
          //
          // Причина двойной записи: race — ChannelProvider mount делает
          // useState(localStorage.getItem('tgx-selected-channel-uuid')) синхронно,
          // а magicLogin.then() — async. Между reload и channels-loaded возможен
          // window когда selectedUuid уже был установлен из stale localStorage
          // (пред-magic-link канал) → fallback на channels[0]. С отдельным
          // priority-key эта дыра закрывается: ChannelProvider всегда сначала
          // смотрит на magic-target и переопределяет любой ранее выбранный канал.
          if (res.channelUUID) {
            try {
              localStorage.setItem('tgx-selected-channel-uuid', res.channelUUID)
              localStorage.setItem('tgx-magic-target-channel', res.channelUUID)
            } catch {}
          }
          // Запоминаем campaignId на всю сессию — будет прилеплен ко всем
          // последующим track() событиям онбординга для воронкового анализа.
          rememberCampaign(res.campaignId)

          // ВАЖНО: track() стреляем СРАЗУ, без ожидания /me, чтобы не потерять
          // event при быстром закрытии страницы / unload (keepalive не 100%).
          // maxAdminID добавится в follow-up события через setUserContext ниже.
          // Этот же event может задублиться из OnboardingShell-fallback'а — на
          // read-side всегда делаем count DISTINCT sessionId, дубль не страшен.
          track('magic_login_ok', {
            userState: res.userState,
            channelUUID: res.channelUUID,
            campaignId: res.campaignId,
            redirect: res.redirect,
            source: 'auth-context',
            message: `magic-login ok · ${res.userState || '—'} → ${res.redirect || '/dashboard'}`,
          })

          // Дёргаем /me чтобы зафиксить maxAdminID для всех track() в этой
          // pre-reload сессии. Если упадёт — не страшно, после reload AuthProvider
          // снова дёрнет refetch() и context подцепится.
          try {
            const me = await getMe()
            setUserContext({ maxAdminID: me.maxAdminID })
          } catch {}

          // F-side polls-flow routing — уважаем `res.intent` от B (contract-doc §4.4,
          // 2026-07-28). F владеет путями (`/onboarding/polls/template`, etc); B
          // передаёт intent, F маршрутизирует. Для не-polls intent'ов идём по
          // `res.redirect` как раньше.
          const urlParams = new URLSearchParams(window.location.search)
          const refPollId = urlParams.get('ref')
          let target = res.redirect || '/dashboard'
          switch (res.intent) {
            case 'polls:onboard-channel': {
              setPollsFlow(refPollId)
              // n=0: сразу в connect-source (SourcePage), не welcome-intro.
              // После connect success ConnectDonePage вернёт юзера в wizard
              // если `?ref=` sticky в URL (см. TODO F-post-connect).
              target = refPollId
                ? `/onboarding/source?ref=${encodeURIComponent(refPollId)}`
                : '/onboarding/source'
              break
            }
            case 'polls:create': {
              const qs = new URLSearchParams()
              if (res.channelUUID) qs.set('channelUUID', res.channelUUID)
              if (refPollId) qs.set('ref', refPollId)
              target = `/onboarding/polls/template?${qs.toString()}`
              break
            }
            case 'polls:channel-picker': {
              target = refPollId
                ? `/polls/channel-picker?ref=${encodeURIComponent(refPollId)}`
                : '/polls/channel-picker'
              break
            }
            case 'polls:analytics': {
              // Автор тапнул CTA в webview → magic-link на LK results.
              // Если ref есть — ведём в конкретный опрос, иначе на список.
              target = refPollId ? `/polls/${encodeURIComponent(refPollId)}` : '/polls'
              break
            }
            default: {
              // Fallback safety-net: B не проставил polls-intent, но URL с ?ref=
              // (страховка на случай ещё-не-задеплоенного B-fix).
              if (refPollId) {
                if (res.channelUUID) {
                  const qs = new URLSearchParams({ channelUUID: res.channelUUID, ref: refPollId })
                  target = `/onboarding/polls/template?${qs.toString()}`
                } else {
                  target = `/onboarding/source?ref=${encodeURIComponent(refPollId)}`
                }
              }
            }
          }
          // hard-redirect — браузер перезагрузит страницу уже с auth-cookie
          window.location.replace(target)
        })
        .catch(async (err) => {
          track('magic_login_failed', {
            level: 'warn',
            errorMessage: (err as Error)?.message,
            message: `magic-login failed · ${(err as Error)?.message || 'unknown'}`,
          })
          // ALREADY_USED — типовой кейс: юзер уже кликнул эту ссылку раньше,
          // токен сгорел, но cookie могла остаться валидной. Проверяем — если
          // /me отдал юзера, просто снимаем ?magic= и продолжаем как залогинены,
          // вместо унижающего редиректа на /login?error=magic-expired.
          try {
            await getMe()
            const url = new URL(window.location.href)
            url.searchParams.delete('magic')
            window.location.replace(url.pathname + (url.search ? url.search : ''))
            return
          } catch {}
          window.location.replace('/login?error=magic-expired')
        })
      return // не делаем refetch — страница перезагрузится
    }
    refetch()
  }, [])

  return (
    <AuthContext.Provider value={{ user, isLoading, refetch, logout }}>
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuth must be used inside AuthProvider')
  return ctx
}
